import { useMemo } from 'react';
import phil from 'phil-reg-prov-mun-brgy';
import { Input } from '@mlm/ui';
import type { MemberAddress } from '../lib/api';

// JM-EX4: source the dropdown options from `phil-reg-prov-mun-brgy` (same dataset
// the legacy app used). We expose `PH_REGIONS` + `regionNameForCode` with the
// same shapes as before so call sites in the Settings tab and checkout don't
// break — the only difference is the data source and that province/city/barangay
// are now ALSO dropdowns instead of free-text inputs.

export const PH_REGIONS: Array<{ code: string; name: string }> = [...phil.regions]
  .map((r) => ({ code: r.reg_code, name: r.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

export function regionNameForCode(code?: string): string | undefined {
  return PH_REGIONS.find((r) => r.code === code)?.name;
}

// Required-field validation shared by every address entry surface (profile,
// checkout editor, storefront delivery dialog) so the rules stay in one place.
// Everything is required EXCEPT Address line 2. Province/City/Barangay accept a
// code OR an existing name, so legacy free-text addresses aren't blocked; Region
// requires the code since courier rates resolve off it.
export type AddressField =
  | 'line1'
  | 'regionCode'
  | 'province'
  | 'city'
  | 'barangay'
  | 'postalCode'
  | 'contactNo';
export type AddressErrors = Partial<Record<AddressField, string>>;

export function validateAddress(a: MemberAddress): AddressErrors {
  const errors: AddressErrors = {};
  if (!a.line1?.trim()) errors.line1 = 'Address line 1 is required.';
  if (!a.regionCode) errors.regionCode = 'Region is required.';
  if (!a.provinceCode && !a.province?.trim()) errors.province = 'Province is required.';
  if (!a.cityCode && !a.city?.trim()) errors.city = 'City / Municipality is required.';
  if (!a.barangayCode && !a.barangay?.trim()) errors.barangay = 'Barangay is required.';
  if (!a.postalCode?.trim()) errors.postalCode = 'Postal code is required.';
  if (!a.contactNo?.trim()) errors.contactNo = 'Contact number is required.';
  return errors;
}

export function isAddressComplete(a: MemberAddress): boolean {
  return Object.keys(validateAddress(a)).length === 0;
}

/** One-line summary of an address, for compact display (e.g. checkout). */
export function formatAddressLine(a: MemberAddress | null | undefined): string {
  if (!a) return '';
  const parts = [
    a.line1,
    a.barangay,
    a.city,
    a.province,
    regionNameForCode(a.regionCode) ?? a.region,
  ]
    .map((p) => p?.trim())
    .filter(Boolean);
  return parts.join(', ');
}

/**
 * Reusable shipping-address field set with cascading PH dropdowns:
 * Region → Province → City/Municipality → Barangay. Each parent change clears
 * the descendants. Free-text legacy addresses still pre-fill the name-only
 * fields; the matching code is filled in on first interaction.
 */
export function AddressFields({
  value,
  onChange,
  errors,
}: {
  value: MemberAddress;
  onChange: (next: MemberAddress) => void;
  /** Per-field validation errors to surface inline (from `validateAddress`).
   * Pass after a submit attempt so fields don't error before interaction. */
  errors?: AddressErrors;
}) {
  const provinces = useMemo(
    () =>
      value.regionCode
        ? phil
            .getProvincesByRegion(value.regionCode)
            .map((p) => ({ code: p.prov_code, name: p.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [value.regionCode],
  );
  const cities = useMemo(
    () =>
      value.provinceCode
        ? phil
            .getCityMunByProvince(value.provinceCode)
            .map((c) => ({ code: c.mun_code, name: c.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [value.provinceCode],
  );
  const barangays = useMemo(
    () =>
      value.cityCode
        ? phil
            .getBarangayByMun(value.cityCode)
            .map((b, i) => ({ code: b.brgy_code ?? `${value.cityCode}-${i}`, name: b.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [value.cityCode],
  );

  function setRegion(code: string) {
    onChange({
      ...value,
      regionCode: code,
      region: PH_REGIONS.find((r) => r.code === code)?.name,
      provinceCode: undefined,
      province: undefined,
      cityCode: undefined,
      city: undefined,
      barangayCode: undefined,
      barangay: undefined,
    });
  }
  function setProvince(code: string) {
    onChange({
      ...value,
      provinceCode: code,
      province: provinces.find((p) => p.code === code)?.name,
      cityCode: undefined,
      city: undefined,
      barangayCode: undefined,
      barangay: undefined,
    });
  }
  function setCity(code: string) {
    onChange({
      ...value,
      cityCode: code,
      city: cities.find((c) => c.code === code)?.name,
      barangayCode: undefined,
      barangay: undefined,
    });
  }
  function setBarangay(code: string) {
    onChange({
      ...value,
      barangayCode: code,
      barangay: barangays.find((b) => b.code === code)?.name,
    });
  }

  const set = (k: keyof MemberAddress, v: string) => onChange({ ...value, [k]: v });

  return (
    <div className="space-y-3">
      <Field label="Address line 1" required error={errors?.line1}>
        <Input value={value.line1 ?? ''} onChange={(e) => set('line1', e.target.value)} />
      </Field>
      <Field label="Address line 2">
        <Input value={value.line2 ?? ''} onChange={(e) => set('line2', e.target.value)} />
      </Field>
      <Field label="Region" required error={errors?.regionCode}>
        <Select
          value={value.regionCode ?? ''}
          options={PH_REGIONS}
          placeholder="Select region…"
          onChange={setRegion}
        />
      </Field>
      <Field label="Province" required error={errors?.province}>
        <Select
          value={value.provinceCode ?? ''}
          options={provinces}
          placeholder={value.regionCode ? 'Select province…' : 'Pick a region first'}
          onChange={setProvince}
          disabled={!value.regionCode}
        />
        {value.province && !value.provinceCode && (
          <p className="mt-1 text-xs text-muted-foreground">
            Current: {value.province} — pick a region above to refresh.
          </p>
        )}
      </Field>
      <Field label="City / Municipality" required error={errors?.city}>
        <Select
          value={value.cityCode ?? ''}
          options={cities}
          placeholder={value.provinceCode ? 'Select city…' : 'Pick a province first'}
          onChange={setCity}
          disabled={!value.provinceCode}
        />
        {value.city && !value.cityCode && (
          <p className="mt-1 text-xs text-muted-foreground">Current: {value.city}</p>
        )}
      </Field>
      <Field label="Barangay" required error={errors?.barangay}>
        <Select
          value={value.barangayCode ?? ''}
          options={barangays}
          placeholder={value.cityCode ? 'Select barangay…' : 'Pick a city first'}
          onChange={setBarangay}
          disabled={!value.cityCode}
        />
        {value.barangay && !value.barangayCode && (
          <p className="mt-1 text-xs text-muted-foreground">Current: {value.barangay}</p>
        )}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Postal code" required error={errors?.postalCode}>
          <Input
            value={value.postalCode ?? ''}
            onChange={(e) => set('postalCode', e.target.value)}
          />
        </Field>
        <Field label="Contact #" required error={errors?.contactNo}>
          <Input
            value={value.contactNo ?? ''}
            onChange={(e) => set('contactNo', e.target.value)}
            placeholder="Required for delivery"
          />
        </Field>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  required,
  error,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  error?: string;
}) {
  return (
    <div>
      <label className="text-xs text-muted-foreground">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}

function Select({
  value,
  options,
  placeholder,
  onChange,
  disabled,
}: {
  value: string;
  options: Array<{ code: string; name: string }>;
  placeholder: string;
  onChange: (code: string) => void;
  disabled?: boolean;
}) {
  return (
    <select
      className="w-full h-9 rounded-md border bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.code} value={o.code}>
          {o.name}
        </option>
      ))}
    </select>
  );
}
